/**
 * changelog/types.ts — the standardized update, and why it cannot drift.
 *
 * The product requirement is that every update, from every user, reads as though one hand wrote it:
 * short, low-noise, trivia omitted, bullets, icons, plain English for a non-technical reader.
 *
 * That is enforced by three mechanisms in order, only one of which involves the model:
 *
 *   1. A MECHANICAL filter drops, before the model sees anything, what is not news by construction (a
 *      merge with no diff of its own, generated artefacts) or by the job owner's own instruction (a
 *      `[skip changelog]` marker, `ignore_paths`, `ignore_authors`). Since 6 August 2026 it makes no
 *      editorial judgement at all — that moved to the model, which returns a verdict per commit.
 *   2. Schema-constrained output: the model returns only the fields below, plus a verdict array whose
 *      only leaves are an integer and a two-member enum. There is no field in which prose could arrive,
 *      so there is no path by which formatting could drift.
 *   3. A deterministic renderer owns the header, the ordering, the caps, the footer and the ICONS.
 *
 * The model's only job is to say WHAT CHANGED, in plain English, and which commits were worth saying it
 * about. It never sees the template, never chooses an icon, and never writes a header. There is no
 * per-user prompt, no tone setting and no free-text style field anywhere in the schema — users control
 * inputs and destinations only.
 */

/** The eight categories. Ordered here by REPORTING RANK: what a reader most needs to know comes first. */
export const CATEGORIES = [
  "breaking",
  "security",
  "removed",
  "added",
  "fixed",
  "improved",
  "performance",
  "maintenance",
] as const;

export type Category = (typeof CATEGORIES)[number];

/**
 * The icon vocabulary. The APP owns this map; the model never emits an emoji, so the vocabulary cannot
 * drift between users or over time. Changing an icon here changes every update everywhere, which is
 * exactly the property the owner asked for.
 */
export const CATEGORY_ICONS: Readonly<Record<Category, string>> = {
  added: "✨",
  improved: "⚡",
  fixed: "🐛",
  removed: "🗑️",
  security: "🔒",
  performance: "🚀",
  breaking: "⚠️",
  maintenance: "🧹",
};

/** Plain-English gloss shown in the UI legend, so a reader never has to guess what an icon means. */
export const CATEGORY_LABELS: Readonly<Record<Category, string>> = {
  added: "New thing you can now do",
  improved: "Something existing works better",
  fixed: "Something broken now works",
  removed: "Something is gone",
  security: "Security or privacy",
  performance: "Faster",
  breaking: "Needs action from you",
  maintenance: "Behind-the-scenes upkeep",
};

/** Rank for ordering. Lower sorts first. Readers care what matters, not when it happened. */
export const CATEGORY_RANK: Readonly<Record<Category, number>> = Object.fromEntries(
  CATEGORIES.map((c, i) => [c, i]),
) as Record<Category, number>;

/** Hard limits, enforced in code after the model returns — never merely requested in the prompt. */
export const LIMITS = {
  /** Maximum bullets in one update. Beyond this the rest become a muted "+N smaller changes" line. */
  maxItems: 8,
  /** `audience: "technical"` items sink below the rest and are capped at this many. */
  maxTechnicalItems: 2,
  headlineMaxChars: 90,
  detailMaxChars: 140,
} as const;

/** One rendered bullet. This is exactly what the model may return — there is no free-text field. */
export interface ChangelogItem {
  readonly category: Category;
  /** Plain English, sentence case, no trailing full stop, no jargon. */
  readonly headline: string;
  readonly detail: string | null;
  readonly repoPath: string;
  readonly audience: "everyone" | "technical";
}

/** What the model returns, before the app's own validation and capping. */
export interface ChangelogDraft {
  readonly items: readonly ChangelogItem[];
  readonly omittedNote: string | null;
}

/**
 * The counters that let a reader tell a quiet week from a broken job. App-generated, never model-written.
 *
 * `commitsDropped` and `commitsRoutine` are two POPULATIONS and must never be conflated. The first is
 * what the mechanical filter removed — bots, lockfiles, empty merges, the job's own ignore lists. The
 * second is what the model read and judged not worth reporting. Together with the items published they
 * account for every commit in the window when nothing was truncated, which is what makes the footer a
 * complete statement rather than a partial one.
 */
export interface UpdateFooter {
  readonly commitsReviewed: number;
  readonly commitsDropped: number;
  /** Read by the model and judged not worth reporting. Zero when nothing reached a model call. */
  readonly commitsRoutine: number;
  readonly reposCovered: number;
  readonly reposFailed: number;
  readonly itemsHidden: number;
  readonly truncated: boolean;
}

/** A complete, renderable update. The renderer turns this into Confluence storage XHTML or Slack text. */
export interface RenderedUpdate {
  /** App-generated: "{cadence} update — {job name} — {window}". Never model-written. */
  readonly heading: string;
  readonly windowStart: string | null;
  readonly windowEnd: string;
  readonly items: readonly ChangelogItem[];
  readonly footer: UpdateFooter;
  readonly omittedNote: string | null;
}

/**
 * Words that must never appear in a headline. The audience is non-technical, so a headline containing
 * these is a headline written for the wrong reader; it is rejected and the item is dropped rather than
 * published. Extended by config, never shortened silently.
 */
export const JARGON_BLOCKLIST: readonly string[] = [
  "refactor",
  "bump",
  "implement",
  "migrate",
  "endpoint",
  "middleware",
  "regression",
  "backport",
  "changeset",
  "config",
  "repo",
  "commit",
  "merge",
  "branch",
  "deploy",
  "dependency",
  "lint",
];
